import { gql, useSubscription } from "@apollo/client";
import {
  AutopilotPreview_AutopilotSessionPreviewFragmentDoc,
  AutopilotPreviewContactStep_AutopilotSessionPreviewContactStepFragmentDoc,
  AutopilotPreviewSteps_InternalAutopilotSessionPreviewSubscription,
  AutopilotPreviewSteps_InternalAutopilotSessionPreviewSubscriptionVariables,
  CuratedSearchDesignTextOnlyStep_AutopilotSearchDesignTextOnlyStepFragmentDoc,
  InternalAutopilotSessionPreviewStep,
} from "../../__generated__/graphql";
import { useEffect, useState } from "react";
import classNames from "classnames";
import { SparklesIcon } from "@heroicons/react/24/outline";
import CuratedSearchDesignTextOnlyStep, {
  CuratedSearchDesignTextOnlyStep_AutopilotSearchDesignTextOnlyStep,
} from "../autopilotCuratedSearch/CuratedSearchDesignSteps/CuratedSearchDesignStep/CuratedSearchDesignTextOnlyStep";
import {
  useFragment as getFragmentData,
  makeFragmentData,
} from "../../__generated__";
import AutopilotPreview, {
  AutopilotPreview_AutopilotSessionPreview,
} from "./AutopilotPreview";
import AutopilotPreviewContactStep, {
  AutopilotPreviewContactStep_AutopilotSessionPreviewContactStep,
} from "./AutopilotPreviewContactStep";
import CiroAlert from "../shared/CiroAlert";

const AutopilotPreviewSteps_InternalAutopilotSessionPreview = gql`
  subscription AutopilotPreviewSteps_InternalAutopilotSessionPreview(
    $curatedSearchId: Int!
  ) {
    internalAutopilotSessionPreview(curatedSearchId: $curatedSearchId) {
      ... on AutopilotSearchDesignTextOnlyStep {
        ephemeral
        ...CuratedSearchDesignTextOnlyStep_AutopilotSearchDesignTextOnlyStep
      }
      ... on AutopilotSessionPreviewResponseStep {
        ephemeral
        autopilotSessionPreviewResponseStepData {
          ...AutopilotPreview_AutopilotSessionPreview
        }
        ephemeral
      }
      ... on AutopilotSessionPreviewContactStep {
        autopilotSessionPreviewContactStepData {
          ...AutopilotPreviewContactStep_AutopilotSessionPreviewContactStep
        }
        ephemeral
      }
    }
  }

  ${CuratedSearchDesignTextOnlyStep_AutopilotSearchDesignTextOnlyStep}
  ${AutopilotPreview_AutopilotSessionPreview}
  ${AutopilotPreviewContactStep_AutopilotSessionPreviewContactStep}
`;

const AutopilotPreviewSteps = ({
  curatedSearchId,
}: {
  curatedSearchId: number | null;
}) => {
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { data } = useSubscription<
    AutopilotPreviewSteps_InternalAutopilotSessionPreviewSubscription,
    AutopilotPreviewSteps_InternalAutopilotSessionPreviewSubscriptionVariables
  >(AutopilotPreviewSteps_InternalAutopilotSessionPreview, {
    variables: {
      curatedSearchId: curatedSearchId || 0,
    },
    skip: !curatedSearchId,
    onComplete: () => {
      setIsComplete(true);
    },
    onError: (error) => {
      console.error("Error in AutopilotPreviewSteps:", error);
      setError(error);
    },
  });

  const [processedSteps, setProcessedSteps] = useState<
    InternalAutopilotSessionPreviewStep[]
  >([]);

  useEffect(() => {
    if (data?.internalAutopilotSessionPreview) {
      const step =
        data.internalAutopilotSessionPreview as InternalAutopilotSessionPreviewStep;
      if (
        "textOnlyData" in step ||
        "autopilotSessionPreviewResponseStepData" in step ||
        "autopilotSessionPreviewContactStepData" in step
      ) {
        setProcessedSteps((previous) => [...previous, step]);
      }
    }
  }, [data]);

  useEffect(() => {
    if (!curatedSearchId) {
      setProcessedSteps([]);
      setError(null);
    }
  }, [curatedSearchId]);

  if (!curatedSearchId) {
    return null;
  }

  return (
    <div className={classNames("w-full", "pt-8")}>
      {error && (
        <CiroAlert
          message={`Error loading preview. Please try again or contact help@ciro.io.`}
          level="error"
        />
      )}
      <div className={classNames("flex", "gap-2", "items-center", "pb-4")}>
        <SparklesIcon className={classNames("w-4", "h-4", "text-orange-500")} />
        <div className={classNames("text-md", "font-semibold")}>
          {isComplete ? "Reviewing sample contacts" : "Review sample contacts"}
        </div>
      </div>
      <div className={classNames("flex", "flex-col", "gap-2")}>
        {processedSteps.map((step, idx) => {
          if (step.ephemeral && idx !== processedSteps.length - 1) {
            return null;
          }

          if (step.__typename === "AutopilotSearchDesignTextOnlyStep") {
            return (
              <CuratedSearchDesignTextOnlyStep
                key={idx}
                isCurrentStep={idx === processedSteps.length - 1}
                data={getFragmentData(
                  CuratedSearchDesignTextOnlyStep_AutopilotSearchDesignTextOnlyStepFragmentDoc,
                  makeFragmentData(
                    step,
                    CuratedSearchDesignTextOnlyStep_AutopilotSearchDesignTextOnlyStepFragmentDoc,
                  ),
                )}
              />
            );
          }
          if (step.__typename === "AutopilotSessionPreviewResponseStep") {
            return (
              <AutopilotPreview
                key={idx}
                autopilotSessionPreview={getFragmentData(
                  AutopilotPreview_AutopilotSessionPreviewFragmentDoc,
                  makeFragmentData(
                    step.autopilotSessionPreviewResponseStepData,
                    AutopilotPreview_AutopilotSessionPreviewFragmentDoc,
                  ),
                )}
                curatedSearchId={curatedSearchId}
              />
            );
          }
          if (step.__typename === "AutopilotSessionPreviewContactStep") {
            return (
              <div key={idx} className={classNames("mt-1")}>
                <AutopilotPreviewContactStep
                  contact={getFragmentData(
                    AutopilotPreviewContactStep_AutopilotSessionPreviewContactStepFragmentDoc,
                    makeFragmentData(
                      step.autopilotSessionPreviewContactStepData,
                      AutopilotPreviewContactStep_AutopilotSessionPreviewContactStepFragmentDoc,
                    ),
                  )}
                />
              </div>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
};

export default AutopilotPreviewSteps;
